import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, MapPin, Quote as QuoteIcon, AlertTriangle, Lightbulb, TrendingUp } from "lucide-react";
import { FINAL_CTA } from "../mock";
import { Reveal, DarkCTA, CtaLink } from "../components/Shared";
import { fetchCaseStudies, CaseCard, Metrics } from "./CaseStudies";
import { mediaUrl, API_BASE } from "../api";
import { Seo, breadcrumbLd } from "../components/Seo";
import { ShareBar } from "../components/ShareBar";

const SECTIONS = [
  { key: "challenge", label: "The challenge", icon: AlertTriangle },
  { key: "solution", label: "The solution", icon: Lightbulb },
  { key: "results", label: "The results", icon: TrendingUp },
];

const CaseStudy = () => {
  const { slug } = useParams();
  const [all, setAll] = useState(null);

  useEffect(() => {
    fetchCaseStudies().then(setAll).catch(() => setAll([]));
  }, []);

  const cs = all?.find((c) => c.slug === slug);

  if (all === null) return <div className="min-h-[60vh]" data-testid="case-loading" />;
  if (!cs) return <Navigate to="/case-studies" replace />;

  const related = all.filter((c) => c.slug !== cs.slug).slice(0, 3);
  const sections = SECTIONS.filter((s) => cs[s.key]?.trim());

  return (
    <>
      <Seo title={cs.seoTitle || `${cs.company} customer story — ${cs.title}`} description={cs.seoDescription || cs.summary} image={cs.ogImage || cs.cover} type="article" markdownUrl={`${API_BASE}/api/md/case-studies/${cs.slug}`} jsonLd={breadcrumbLd([{ name: "Customer stories", path: "/case-studies" }, { name: cs.company, path: `/case-studies/${cs.slug}` }])} />
      <article>
        <header className="pt-12 md:pt-16 pb-10">
          <div className="container-x">
            <Link to="/case-studies" className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--fr-muted)] hover:text-[var(--fr-ink)]" data-testid="case-back">
              <ArrowLeft size={15} /> All customer stories
            </Link>
            <div className="mt-8 grid lg:grid-cols-[1fr_380px] gap-10 items-end">
              <div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--fr-muted)] fade-up">
                  <span className="chip">{cs.sector}</span>
                  {cs.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={13} /> {cs.location}
                    </span>
                  )}
                </div>
                <div className="mt-5 flex items-center gap-4 fade-up delay-1">
                  {cs.logo && <img src={mediaUrl(cs.logo)} alt={cs.company} className="h-10 w-auto max-w-[160px] object-contain" data-testid="case-logo" />}
                  <span className="font-display font-bold text-lg" data-testid="case-company">{cs.company}</span>
                </div>
                <h1 className="font-display font-bold tracking-tight leading-[1.08] text-3xl md:text-5xl mt-5 fade-up delay-2" data-testid="case-title">
                  {cs.title}
                </h1>
                <p className="mt-5 text-lg text-[var(--fr-ink-2)] leading-relaxed max-w-2xl fade-up delay-3">{cs.summary}</p>
              </div>
              {cs.metrics?.length > 0 && (
                <Reveal className="dark-block !rounded-[20px] p-7" data-testid="case-metrics">
                  <div className="text-xs uppercase tracking-[0.14em] text-white/60 mb-5">Outcomes</div>
                  <Metrics metrics={cs.metrics} dark />
                </Reveal>
              )}
            </div>
          </div>
        </header>

        {cs.cover && (
          <div className="container-x">
            <Reveal className="card p-3">
              <div className="img-tile aspect-[21/9]">
                <img src={mediaUrl(cs.cover)} alt={cs.company} />
              </div>
            </Reveal>
          </div>
        )}

        <div className="container-x max-w-[900px] py-16">
          {sections.map(({ key, label, icon: Icon }, i) => (
            <Reveal key={key} className={`grid md:grid-cols-[200px_1fr] gap-6 md:gap-10 ${i > 0 ? "mt-14 pt-14 border-t border-[var(--fr-border)]" : ""}`} data-testid={`case-section-${key}`}>
              <div className="flex md:flex-col items-center md:items-start gap-3">
                <span className="w-10 h-10 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
                  <Icon size={18} />
                </span>
                <h2 className="font-display font-bold text-xl md:text-2xl">{label}</h2>
              </div>
              <div className="prose-fr">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{cs[key]}</ReactMarkdown>
              </div>
            </Reveal>
          ))}

          {cs.quote?.text && (
            <Reveal className="mt-16 card p-8 md:p-10 bg-[var(--fr-primary-tint)]/40 border-[var(--fr-primary-tint)]" data-testid="case-quote">
              <QuoteIcon size={28} className="text-[var(--fr-primary)]" />
              <p className="mt-4 font-display text-xl md:text-2xl leading-snug">“{cs.quote.text}”</p>
              <div className="mt-5 text-sm">
                <span className="font-semibold">{cs.quote.name}</span>
                {cs.quote.role && <span className="text-[var(--fr-muted)]"> · {cs.quote.role}</span>}
              </div>
            </Reveal>
          )}

          <div className="mt-12 pt-8 border-t border-[var(--fr-border)]">
            <ShareBar kind="case-studies" slug={cs.slug} title={`${cs.company} — ${cs.title}`} />
          </div>

          <Reveal className="mt-10 card p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1">
              <h3 className="font-display font-bold text-xl">Want results like {cs.company}?</h3>
              <p className="mt-1 text-[15px] text-[var(--fr-ink-2)]">Talk to a specialist or start a free trial — live in days, not months.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/contact" className="btn btn-outline" data-testid="case-demo-cta">
                Request a demo
              </Link>
              <CtaLink cta={FINAL_CTA.cta} className="btn btn-primary" data-testid="case-trial-cta" />
            </div>
          </Reveal>
        </div>
      </article>

      {related.length > 0 && (
        <section className="section bg-[var(--fr-bg-alt)]" data-testid="case-related">
          <div className="container-x">
            <div className="flex items-end justify-between gap-6">
              <h2 className="h-section">More customer stories</h2>
              <Link to="/case-studies" className="text-sm font-semibold text-[var(--fr-primary-dark)] hover:underline whitespace-nowrap">
                All stories →
              </Link>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((c, i) => (
                <CaseCard key={c.slug} cs={c} delay={i * 70} />
              ))}
            </div>
          </div>
        </section>
      )}

      <DarkCTA title={FINAL_CTA.title} text={FINAL_CTA.text} cta={FINAL_CTA.cta} secondary={FINAL_CTA.secondary} email={FINAL_CTA.email} />
    </>
  );
};

export default CaseStudy;
